import { isEmpty } from 'lodash-es';

import {
  useFetchCommunityReviewReplies,
  useUpdateReviewReaction,
} from '@/actions/community-review.actions.js';
import { ProfileTypes } from '@/constants/profileType.constant';
import { cn } from '@/lib/utils';
import getQualifiedImageUrl from '@/utils/imageUrlProcessor';
import Avatar from '@ui/Avatar';
import Button from '@ui/Button';
import Link from '@ui/Link';
import Typography from '@ui/Typography';
import { Separator } from '@ui/separator';
import { LucideLoader } from 'lucide-react';
import Image from 'next/image';
import PropTypes from 'prop-types';
import React from 'react';
import AppLink from '../../next-ui/Link/Link';
import ImageViewDialog from '../ImageViewDialog';
import CommunityReviewForm from './CommunityReviewForm';
import CommunityReviewMenu from './CommunityReviewMenu';
import PostMessage from '../PostMessage';
import Reactions from '../Reactions';
import TimeSincePost from '../TimeSincePost';
import useUserProfile from '@/hooks/useUserProfile';
import { Rating } from '@mui/material';
import { useIsNonProfitAdminFor } from '@/hooks/useUser';
import ReviewReactionButton from './ReviewReactionButton';
import { useBreakpointHooks } from '@/utils/break-points.provider';
import { cx } from 'cva';
import useHasUserSession from '@/hooks/useHasUserSession';
import { getJobTitle } from '@/utils/user.helper';

const AuthorChip = () => {
  const isLessThanSmallScreen = useBreakpointHooks((bp) => bp.useSmaller('sm'));

  return (
    <div
      className={cx(
        'max-w-fit rounded-md bg-text-secondary px-1 py-0.5',
        isLessThanSmallScreen ? '' : 'ml-2',
      )}>
      <Typography variant="caption" color="white" className="font-medium">
        Author
      </Typography>
    </div>
  );
};

export default function CommunityReview(props) {
  const {
    nonProfitId,
    nonProfit,
    review,
    onStateChange = () => {},
    disabled = false,
    refetchUserReview = () => {},
    handleRedirect,
  } = props;
  const userProfile = useUserProfile();
  const hasUserSession = useHasUserSession();

  const [status, setStatus] = React.useState(null);
  const [replyAnimation, setReplyAnimation] = React.useState({ blink: false });
  const [editing, setEditing] = React.useState(false);
  const [showImageInFull, setShowImageInFull] = React.useState(false);

  const isChildReview = !isEmpty(review.parentReviewId);
  const isAdminOfNPO = useIsNonProfitAdminFor(review?.nonProfitId);
  // TODO: check
  // const isAuthor =
  //   post.author.id === review.authorId && post.postAs === review.postAs;
  const isAuthor = userProfile.id === review.authorId;
  const canReplyToReview = true;

  const {
    data,
    isLoading: fetchingReplies,
    hasNextPage,
    fetchNextPage,
  } = useFetchCommunityReviewReplies(nonProfitId, review);

  const { mutateAsync: updateReaction } = useUpdateReviewReaction();

  // derived states

  // const isNonProfitAdmin = useStore((state) => state.user.isNonProfitAdmin);
  // const hasReplies =
  //   useStore((state) => state.socialFeed.hasMoreRepliesByComment[comment.id]) ||
  //   false;

  // const isEditing =
  //   useStore((state) => state.socialFeed.editingCommentId) === comment.id;
  // const nonProfit =
  //   useStore((state) => state.nonProfit.cached[comment.author.nonProfitId]) ||
  //   null;

  // const jobTitle = isNonProfitAdmin
  //   ? `${comment.author.designation} - ${comment.author.orgDetails.name}`
  //   : comment.author.designation;
  // const replyHelperText = '';

  const getAttachmentUrl = () => {
    if (review.attachmentUrl) {
      if (review.attachmentUrl.startsWith('data:')) {
        return review.attachmentUrl;
      }
      return getQualifiedImageUrl(review.attachmentUrl);
    }
    return null;
  };

  const handleReply = () => {
    if (status === 'reply') {
      setReplyAnimation({ blink: true });
    }

    if (isChildReview) {
      onStateChange('reply', review.parentReviewId);
    } else {
      setStatus('reply');
    }
  };

  const handleChildStateChange = (type) => {
    if (type === 'reply') {
      if (status === 'reply') {
        setReplyAnimation({ blink: true });
      }
      setStatus('reply');
    }
  };

  const handleLoadReplies = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  // const showPostContributorDialog = useStore(
  //   (state) => state.postContributorDialog.showDialog
  // );

  const showReactions = () => {
    // showPostContributorDialog(
    //   { id: comment.postId },
    //   null,
    //   'reactions',
    //   comment
    // );
  };

  const handleReaction = async (reaction) => {
    await updateReaction({ review, reaction });
  };

  const getAuthorSlug = (review) => {
    if (
      review.postAs === ProfileTypes.Organization &&
      review.author.orgDetails?.slug
    ) {
      return `/${review.author.orgDetails.slug}`;
    } else if (
      review.postAs === ProfileTypes.Individual &&
      review.author.slug
    ) {
      return `/${review.author.slug}`;
    }
    return null;
  };

  const renderProfileAvatar = () => {
    switch (review.postAs) {
      case 'org':
        return (
          <Avatar
            src={review.author.orgDetails.avatar}
            alt={review.author.orgDetails.name}
            profile="npo"
            size={isChildReview ? 'sm' : 'sm'}></Avatar>
        );
      case 'me':
        return (
          <Avatar
            src={review.author.avatar}
            alt={review.author.fullName}
            profile="user"
            size={isChildReview ? 'sm' : 'sm'}></Avatar>
        );
      default:
        return null;
    }
  };

  const renderProfileDetails = () => {
    switch (review.postAs) {
      case 'org':
        return (
          <>
            <div className="flex flex-col gap-1 md:flex-row md:gap-0">
              {/* // TODO: check */}
              <Link to={`/nonprofit/${review.author.nonProfitId}`}>
                <Typography variant="h5" className="hover:text-text-hyperlink">
                  {review.author.orgDetails.name}
                </Typography>
              </Link>
              {isAuthor && <AuthorChip />}
            </div>
            {review.rating && (
              <Rating
                name="read-only"
                value={review.rating}
                precision={0.1}
                readOnly
                className="text-primary"
              />
            )}
          </>
        );
      case 'me':
        return (
          <div className="flex flex-col">
            <div className="flex flex-col gap-1 md:flex-row md:gap-0">
              <AppLink href={getAuthorSlug(review)}>
                <Typography variant="h5" className="hover:text-text-hyperlink">
                  {review.author.fullName}
                </Typography>
              </AppLink>
              {isAuthor && <AuthorChip />}
            </div>
            <Typography variant="caption" color="textDisabled">
              {getJobTitle(review.author)}
            </Typography>
            {review.rating && (
              <Rating
                name="read-only"
                value={review.rating}
                precision={0.1}
                readOnly
                className="text-primary"
              />
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex w-full gap-3">
      <div className="pt-1">{renderProfileAvatar()}</div>

      <div className="flex flex-1 flex-col gap-1">
        <div className="flex flex-col">
          <div className="flex flex-col gap-1 rounded-md bg-body-background p-2">
            <div className="flex items-center justify-between">
              <div className="flex flex-col items-start">
                {renderProfileDetails()}
                <div className="md:hidden">
                  <TimeSincePost
                    variant="caption"
                    createdOn={review.createdOn}
                  />
                </div>
              </div>
              <div className="flex items-start gap-1">
                <div className="mr-2 hidden md:block">
                  <TimeSincePost
                    variant="caption"
                    createdOn={review.createdOn}
                  />
                </div>

                <CommunityReviewMenu
                  review={review}
                  onEdit={() => setEditing(true)}
                  nonProfit={nonProfit}
                  handleRedirect={handleRedirect}
                />
              </div>
            </div>
            {status === 'loading' ? (
              <div className="ml-2">
                <LucideLoader className="h-4 w-4 animate-spin text-text-disabled" />
              </div>
            ) : (
              <>
                {editing && (
                  <CommunityReviewForm
                    review={review}
                    nonProfitId={review.nonProfitId}
                    mode="edit"
                    onComplete={() => setEditing(false)}
                  />
                )}

                {!editing && (
                  <>
                    {<PostMessage message={review.message} />}

                    {review.attachmentUrl && (
                      <>
                        <div className="mt-2 grid w-full grid-cols-1 pl-2 sm:grid-cols-2 md:grid-cols-3">
                          <div
                            className="relative h-32 w-full"
                            role="button"
                            onClick={() => setShowImageInFull(true)}>
                            <Image
                              fill
                              sizes="(max-width: 640px) 100vw, 640px"
                              src={getAttachmentUrl() || ''}
                              alt="User uploaded image"
                              className="rounded-md object-cover object-center"
                            />
                          </div>
                        </div>
                        <ImageViewDialog
                          isOpen={showImageInFull}
                          imageUrl={getAttachmentUrl()}
                          onClose={() => setShowImageInFull(false)}
                        />
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <div className="flex items-center gap-3 py-1">
            {!disabled && (
              <ReviewReactionButton
                userReaction={review.userReaction}
                onChange={handleReaction}
                variant="label"
                nonProfit={nonProfit}
                handleRedirect={handleRedirect}
              />
            )}
            {review.totalReactions > 0 && (
              <div className="group-hover">
                <Reactions reactions={review.reactions} onClick={showReactions}>
                  <Typography variant="caption" ml={0.5}>
                    {review.totalReactions}
                  </Typography>
                </Reactions>
              </div>
            )}
            {!disabled && canReplyToReview && (
              <>
                <Separator orientation="vertical" />
                {hasUserSession ? (
                  <Button variant="plain" size="xs" onClick={handleReply}>
                    <Typography variant="caption" color="textSecondary">
                      Reply
                    </Typography>
                  </Button>
                ) : (
                  <Button
                    variant="plain"
                    size="xs"
                    onClick={() => handleRedirect()}>
                    <Typography variant="caption" color="textSecondary">
                      Reply
                    </Typography>
                  </Button>
                )}
              </>
            )}
            {review.totalReplies > 0 && (
              <Typography
                variant="caption"
                color="textDisabled"
                className="py-0.5">
                {review.totalReplies} Replies
              </Typography>
            )}
          </div>
        </div>
        <div className="flex flex-col items-start gap-4">
          {!isChildReview && hasNextPage && (
            <div className="flex items-center gap-2">
              <button onClick={handleLoadReplies} disabled={fetchingReplies}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  className={cn(fetchingReplies ? '' : 'hover:text-primary')}>
                  See older replies
                </Typography>
              </button>

              {fetchingReplies && (
                <LucideLoader className="h-4 w-4 animate-spin text-primary" />
              )}
            </div>
          )}

          {data?.pages.map((group, i) => (
            <React.Fragment key={i}>
              {group.reviews.map((reply) => (
                <CommunityReview
                  key={reply.id}
                  nonProfit={nonProfit}
                  nonProfitId={nonProfitId}
                  review={reply}
                  disabled={disabled}
                  onStateChange={handleChildStateChange}
                  handleRedirect={handleRedirect}
                />
              ))}
            </React.Fragment>
          ))}
        </div>
        {status === 'reply' && (
          <div className="my-1">
            <CommunityReviewForm
              animation={replyAnimation}
              nonProfitId={review.nonProfitId}
              parentReviewId={review.id}
            />
          </div>
        )}
      </div>
    </div>
  );
}
